import type { Carer } from '@lmc/ts-common'
import {
  CapabilityKey,
  getCapabilityTogglesForRole
} from '../../../../../../components/generic/teamCapabilities'
import type { WizardRole } from '../../../../../../components/generic/teamCapabilities/types'
import { isOnCareManagementBundle } from '../../utils/effectivePermissions'
import type { EditPermissionsData } from './types'

export type EditPermissionsUpdatePayload = {
  role: WizardRole
  permissions: string[]
  medicationPermission: boolean
  // Only present (as null) when converting a carer off the legacy Care Management
  // bundle — see below. Omitted otherwise so the PUT leaves the field untouched.
  permissionsTemplate?: null
}

// Build the PUT body for the Edit-permissions side panel (Path B: each toggle
// owns one permission ObjectId, which it adds/removes individually).
//
// REPLACE SEMANTICS (confirmed with backend): the carer-update PUT
// (`PUT list/carers/{id}` in hooks/useUser/useUser.ts) replaces
// `user.permissions` wholesale with the array we send — Mongoose `Document.set()`
// overwrites the array, there is no $addToSet/$push (care-management
// entities/internal/users/update.ts:446). So omitting an id revokes it. Because
// we replace, we must:
//   1. start from the carer's *existing* permissions (so ids the panel doesn't
//      manage survive the save — privacy/audit grants, or an add-on with no
//      toggle for this role such as Safeguarding on a Senior Carer), then
//   2. strip the toggle permission ids for the carer's *original* role and the
//      *selected* role — so a role change clears the old role's add-ons, while a
//      same-role save only re-decides this role's own toggles (never a managed id
//      that isn't a toggle here), then
//   3. re-add the selected role's toggle ids whose switch is ON.
//
// Backend side-effects to be aware of (not handled here):
//   - The server strips MANAGE_SAFEGUARDING when the target role isn't
//     careHomeAdmin/management — consistent with our toggle config, which only
//     offers Safeguarding Oversight for those roles.
//   - Removing Safeguarding from the last Safeguarding Officer is rejected with
//     400 (min-officers rule) — surfaced via the error toast; the inline warning
//     is CM-3436's scope.
export const buildUpdatePayload = (
  carer: Carer,
  data: EditPermissionsData
): EditPermissionsUpdatePayload => {
  const togglePermissionIds = (role: string): string[] =>
    getCapabilityTogglesForRole(role)
      .map((toggle) => toggle.permissionId)
      .filter((id): id is string => Boolean(id))

  // 1 + 2: drop only the toggle ids this panel manages for the original/selected
  // role; everything else is preserved (incl. a managed id with no toggle for
  // this role, e.g. Safeguarding on a Senior Carer).
  const managedForRoles = new Set([
    ...togglePermissionIds(carer.role),
    ...togglePermissionIds(data.role)
  ])
  const preserved = (carer.permissions ?? [])
    .map(String)
    .filter((id) => !managedForRoles.has(id))

  // 3: add back the selected role's toggle ids whose switch is ON.
  const selected = getCapabilityTogglesForRole(data.role)
    .filter(
      (toggle): toggle is typeof toggle & { permissionId: string } =>
        Boolean(toggle.permissionId) && data.capabilities[toggle.key] === true
    )
    .map((toggle) => toggle.permissionId)

  // De-dupe while preserving insertion order (preserved first, then selected).
  const permissions = [...new Set([...preserved, ...selected])]

  return {
    role: data.role,
    permissions,
    medicationPermission:
      data.capabilities[CapabilityKey.MedicationManagement] === true,
    // Lazy convert-on-edit: when an admin edits a Senior Carer still on the legacy
    // Care Management bundle, clear the bundle so the individual `permissions`
    // above become the single source of truth. buildInitialData seeds the toggles
    // from the carer's *effective* permissions, so `permissions` already reflects
    // the bundle's surfaced add-ons (minus anything just turned off).
    //
    // Clearing the bundle preserves effective access without us re-writing its
    // non-surfaced grants, because each is covered elsewhere:
    //   - Noticeboard, Create Task, Contingency Plans are already in the Senior
    //     Carer role baseline (verified against the backend resolver), so they
    //     survive regardless of the bundle.
    //   - Edit-Documentation-records + archive-risk-assessments are folded into
    //     the Service User Management permission (CM-3517), so they survive as
    //     long as that toggle stays on (turning it off is an intended revoke).
    // DEPLOY DEPENDENCY: this convert must not ship/enable before CM-3517 — without
    // that grant, converting a carer who keeps Service User on would drop those two
    // abilities. The feature is flag-gated; sequence the backend first.
    // No bulk migration — only edited users are converted, one at a time.
    ...(isOnCareManagementBundle(carer) ? { permissionsTemplate: null } : {})
  }
}
